import { Icon } from '@iconify/react';
import KeyboardArrowDownIcon from '@iconify/icons-eva/arrow-ios-downward-fill';
import KeyboardArrowUpIcon from '@iconify/icons-eva/arrow-ios-upward-fill';
import OpenInNewIcon from '@iconify/icons-ic/open-in-new';
import PropTypes from 'prop-types';

import {
  Grid,
  Container,
  Card,
  Collapse,
  Alert,
  Skeleton,
  useTheme,
  Typography,
  IconButton,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import usePeriod from '../hooks/usePeriod';
import PeriodSelector from '../components/PeriodSelector';
import { useAccount } from '../hooks/useAccount';
import MetricCard from '../components/MetricCard';
import Page from '../components/Page';
import ContainerMessage from '../components/ContainerMessage';
import MultiChart from '../components/MultiChart';

const GET_ADVANCED_STATISTICS = gql`
  query MyQuery($domain: ID!, $from: String!, $to: String!) {
    listAdvancedStatistics(domain: $domain, typeDateKey: { between: [$from, $to] }) {
      items {
        domain
        typeDateKey
        type
        dateKey
        createdAt
        updatedAt
        rawSearchCount
        searchCount
        topQueries {
          query
          count
          ctr
          __typename
          topClicked {
            productId
            count
            ctr
            __typename
          }
        }
        topProducts {
          productId
          count
          ctr
          __typename
        }
        __typename
      }
    }
  }
`;

const GET_PRODUCT_DETAIL = gql`
  query MyQuery($domain: ID!, $productId: String!) {
    getProduct(domain: $domain, productId: $productId) {
      productId
      imgLink
      link
      title
      __typename
    }
  }
`;

function accummulateTopQueries(items) {
  const topQueriesOutput = [];

  if (items.length < 1) {
    return {
      topQueries: topQueriesOutput,
      totalCount: 0,
      totalRawSearchCount: 0,
      totalDays: 0,
      topQueriesChartData: {}
    };
  }

  const topQueries = {};
  let totalCount = 0;
  let totalRawSearchCount = 0;
  items.forEach((item) => {
    item.topQueries.forEach((topQuery) => {
      totalCount += item.searchCount;
      totalRawSearchCount += item.rawSearchCount;
      if (!topQueries[topQuery.query]) {
        topQueries[topQuery.query] = {
          count: topQuery.count,
          daysWithQuery: 1,
          topClicked: {}
        };
      } else {
        topQueries[topQuery.query].count += topQuery.count;
        topQueries[topQuery.query].daysWithQuery += 1;
      }
      topQuery.topClicked.forEach((topClicked) => {
        if (!topQueries[topQuery.query].topClicked[topClicked.productId]) {
          topQueries[topQuery.query].topClicked[topClicked.productId] = topClicked.count;
        } else {
          topQueries[topQuery.query].topClicked[topClicked.productId] += topClicked.count;
        }
      });
    });
  });

  Object.keys(topQueries).forEach((query) => {
    const topClickedAccumulated = [];
    Object.keys(topQueries[query].topClicked).forEach((productId) => {
      topClickedAccumulated.push({
        productId,
        count: topQueries[query].topClicked[productId],
        ctr: topQueries[query].topClicked[productId] / topQueries[query].count
      });
    });

    // sort topClickedAccumulated by count descending
    topClickedAccumulated.sort((a, b) => b.count - a.count);

    topQueriesOutput.push({
      query,
      daysWithQuery: topQueries[query].daysWithQuery,
      count: topQueries[query].count,
      ctr: topQueries[query].count / totalCount,
      topClicked: topClickedAccumulated
    });
  });

  // sort topQueriesOutput by count descending
  topQueriesOutput.sort((a, b) => b.count - a.count);
  return {
    topQueries: topQueriesOutput,
    totalCount,
    totalRawSearchCount,
    totalDays: items.length,
    topQueriesChartData: {
      labels: ['2025-11-14', '2025-11-16', '2025-11-17', '2025-11-18'],
      dataSeries: [
        [1.5, 2.5, 3.5, 4.5],
        [1.7, 2.1, 3.1, 4.1],
        [1, 2, 3, 4],
        [1.3, 1.6, 2.2, 2.9]
      ],
      dataTooltip: ['xxx1', 'xxx2', 'xxx3', 'xxx4'],
      seriesLabels: [
        'Test top queries label',
        'Test top queries label 2',
        'Test top queries label 3',
        'Test top queries label 4'
      ]
    }
  };
}

function accummulateTopProducts(items) {
  const topProductsOutput = [];

  if (items.length < 1) {
    return { topProducts: topProductsOutput, totalCount: 0, totalRawSearchCount: 0, totalDays: 0 };
  }

  const topProducts = {};
  let totalCount = 0;
  let totalRawSearchCount = 0;
  items.forEach((item) => {
    item.topProducts.forEach((topProduct) => {
      totalCount += item.searchCount;
      totalRawSearchCount += item.rawSearchCount;
      if (!topProducts[topProduct.productId]) {
        topProducts[topProduct.productId] = {
          count: topProduct.count,
          daysWithProduct: 1
        };
      } else {
        topProducts[topProduct.productId].count += topProduct.count;
        topProducts[topProduct.productId].daysWithProduct += 1;
      }
    });
  });

  Object.keys(topProducts).forEach((productId) => {
    topProductsOutput.push({
      productId,
      daysWithProduct: topProducts[productId].daysWithProduct,
      count: topProducts[productId].count,
      ctr: topProducts[productId].count / totalCount
    });
  });

  // sort topProductsOutput by count descending
  topProductsOutput.sort((a, b) => b.count - a.count);
  return {
    topProducts: topProductsOutput,
    totalCount,
    totalRawSearchCount,
    totalDays: items.length
  };
}

ProductWithLink.propTypes = {
  domain: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired
};

function ProductWithLink(props) {
  const { domain, productId } = props;
  const { data, loading, error } = useQuery(GET_PRODUCT_DETAIL, {
    variables: { domain, productId },
    cacheTime: 5 * 60 * 1000, // 5 minutes
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
  return (
    <>
      {loading && <Skeleton variant="text" width={100} height={20} />}
      {error && <Alert severity="error">Failed to load product detail</Alert>}
      {data && (
        <a
          href={data.getProduct.link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {data.getProduct.title}
          <Icon icon={OpenInNewIcon} fontSize="small" />
        </a>
      )}
    </>
  );
}

TopQueriesRow.propTypes = {
  row: PropTypes.shape({
    query: PropTypes.string.isRequired,
    ctr: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    topClicked: PropTypes.arrayOf(
      PropTypes.shape({
        productId: PropTypes.string.isRequired,
        ctr: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired
      })
    ).isRequired
  }).isRequired,
  domain: PropTypes.string.isRequired
};

function TopQueriesRow(props) {
  const { row, domain } = props;
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <Icon icon={KeyboardArrowUpIcon} /> : <Icon icon={KeyboardArrowDownIcon} />}
          </IconButton>
        </TableCell>
        <TableCell scope="row">{row.query}</TableCell>
        <TableCell align="right">{(row.ctr * 100).toFixed(2)}%</TableCell>
        <TableCell align="right">{row.count}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: 'grey.200' }}>
                  <TableRow>
                    <TableCell>Top clicked product for a query</TableCell>
                    <TableCell align="right">CTR(%)</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.topClicked.map((topClickedRow) => (
                    <TableRow key={topClickedRow.productId}>
                      <TableCell scope="row">
                        <ProductWithLink domain={domain} productId={topClickedRow.productId} />
                      </TableCell>
                      <TableCell align="right">{(topClickedRow.ctr * 100).toFixed(2)}%</TableCell>
                      <TableCell align="right">{topClickedRow.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function SearchOverview() {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    eshop: { domain, showSearch }
  } = useAccount();
  const { from, to, range, dispatch: dispatchPeriod } = usePeriod();
  const {
    loading: loadingQueries,
    error: errorQueries,
    data: dataQueries
  } = useQuery(GET_ADVANCED_STATISTICS, {
    variables: {
      domain,
      from: `topQueries-${from.clone().format('YYYY-MM-DD')}`,
      to: `topQueries-${to.clone().format('YYYY-MM-DD')}`
    },
    skip: !domain
  });

  const {
    loading: loadingProducts,
    error: errorProducts,
    data: dataProducts
  } = useQuery(GET_ADVANCED_STATISTICS, {
    variables: {
      domain,
      from: `topProducts-${from.clone().format('YYYY-MM-DD')}`,
      to: `topProducts-${to.clone().format('YYYY-MM-DD')}`
    },
    skip: !domain
  });

  // Extract and flatten topQueries from all items
  const statisticsQueries = dataQueries?.listAdvancedStatistics?.items
    ? accummulateTopQueries(dataQueries.listAdvancedStatistics.items)
    : {
        topQueries: [],
        totalCount: 0,
        totalRawSearchCount: 0,
        totalDays: 0,
        topQueriesChartData: {}
      };

  // Extract and flatten topProducts from all items
  // collapsable: https://mui.com/material-ui/react-table/
  const statisticsProducts = dataProducts?.listAdvancedStatistics?.items
    ? accummulateTopProducts(dataProducts.listAdvancedStatistics.items)
    : {
        topProducts: [],
        totalCount: 0,
        totalRawSearchCount: 0,
        totalDays: 0
      };

  return (
    <Page title={t('pages.search_overview')}>
      <Container>
        <Box>
          <Typography variant="h4" sx={{ mb: 5 }}>
            {t('general.search-overview')}
          </Typography>
        </Box>
        <Box>
          <PeriodSelector from={from} to={to} range={range} dispatch={dispatchPeriod} />
        </Box>
        {errorQueries && (
          <Alert severity="error">{t(`errors.failed_to_load_queries_statistics`)}</Alert>
        )}
        {errorProducts && (
          <Alert severity="error">{t(`errors.failed_to_load_products_statistics`)}</Alert>
        )}
        {!showSearch && (
          <ContainerMessage
            title={t('overview.search_not_active_title')}
            subtitle={t('overview.search_not_active_description')}
          />
        )}
        {/* show loading skeleton */}
        {(loadingQueries || loadingProducts) && (
          <>
            <Grid container spacing={3}>
              {[...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Skeleton
                    height={175}
                    variant="rectangular"
                    sx={{
                      borderRadius: `${theme.shape.borderRadius}px`
                    }}
                  />
                </Grid>
              ))}
              {[...Array(2)].map((_, index) => (
                <Grid item xs={12} md={12} lg={12} key={index}>
                  <Skeleton
                    height={450}
                    variant="rectangular"
                    sx={{
                      borderRadius: `${theme.shape.borderRadius}px`
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {showSearch &&
          (!loadingQueries || !loadingProducts) &&
          (statisticsQueries.topQueries.length === 0 ||
            statisticsProducts.topProducts.length === 0) && (
            <ContainerMessage
              title={t('overview.not_enough_data_title')}
              subtitle={t('overview.not_enough_data_description')}
            />
          )}
        {showSearch &&
          !loadingQueries &&
          !loadingProducts &&
          (statisticsQueries.topQueries.length > 0 ||
            statisticsProducts.topProducts.length > 0) && (
            <>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <MultiChart
                  title={t('overview.top_queries_chart_title')}
                  seriesLabels={statisticsQueries.topQueriesChartData.seriesLabels}
                  yLabel={t('overview.top_queries_chart_y_label')}
                  dataSeries={statisticsQueries.topQueriesChartData.dataSeries}
                  dataTooltip={statisticsQueries.topQueriesChartData.dataTooltip}
                  labels={statisticsQueries.topQueriesChartData.labels}
                />
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4} md={4}>
                  <MetricCard
                    title="Days in selected period"
                    number={statisticsQueries.totalDays}
                    tooltip="Days in selected period"
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <MetricCard
                    title="Total number of queries"
                    number={statisticsQueries.totalCount}
                    tooltip="Total number of queries"
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <MetricCard
                    title="Total number of raw search queries"
                    number={statisticsQueries.totalRawSearchCount}
                    tooltip="Total number of raw search queries"
                  />
                </Grid>
                {/* Top Queries */}
                <Grid item xs={12} sm={6} md={6}>
                  <TableContainer component={Card}>
                    <Table sx={{ minWidth: 500 }}>
                      <TableHead sx={{ backgroundColor: 'grey.200' }}>
                        <TableRow>
                          <TableCell width="60px" />
                          <TableCell scope="row">Trending queries</TableCell>
                          <TableCell align="right">CTR(%)</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statisticsQueries.topQueries.map((row) => (
                          <TopQueriesRow key={row.query} row={row} domain={domain} />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                {/* Top products */}
                <Grid item xs={12} sm={6} md={6}>
                  <TableContainer component={Card}>
                    <Table sx={{ minWidth: 500 }}>
                      <TableHead sx={{ backgroundColor: 'grey.200' }}>
                        <TableRow>
                          <TableCell>Trending clicked products</TableCell>
                          <TableCell align="right">CTR(%)</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statisticsProducts.topProducts.map((row) => (
                          <TableRow
                            key={row.productId}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell component="th" scope="row">
                              <ProductWithLink domain={domain} productId={row.productId} />
                            </TableCell>
                            <TableCell align="right">{(row.ctr * 100).toFixed(2)}%</TableCell>
                            <TableCell align="right">{row.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </>
          )}
      </Container>
    </Page>
  );
}
